import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, FileText } from "lucide-react";
import { toast } from "sonner";

const SPRAY_IMG =
  "https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmluZyUyMHByb2R1Y3RzJTIwc3VwcGxpZXMlMjBzcHJheSUyMGJvdHRsZSUyMGVjbyUyMGZyaWVuZGx5fGVufDB8fHx8MTc4OTIwNTI4OXww&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop";
const ECO_IMG =
  "https://images.unsplash.com/photo-1528740561666-dc2479dc08ab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBjbGVhbmluZyUyMHByb2R1Y3RzJTIwc3VwcGxpZXMlMjBzcHJheSUyMGJvdHRsZSUyMGVjbyUyMGZyaWVuZGx5fGVufDB8fHx8MTc4OTIwNTI4OXww&ixlib=rb-4.1.0&q=85&w=800&auto=format&fit=crop";

const TABS = ["All", "Disinfectants", "Microfiber", "Degreasers", "Green Tech"];

const PRODUCTS = [
  { id: "aurum", name: "Aurum Pro Disinfectant", cat: "Disinfectants", img: SPRAY_IMG, desc: "EN14476 virucidal concentrate, 5L.", eco: false },
  { id: "steriguard", name: "SteriGuard Electrostatic Mist", cat: "Disinfectants", img: ECO_IMG, desc: "360° wraparound surface coverage.", eco: true },
  { id: "weavemax", name: "WeaveMax Microfiber System", cat: "Microfiber", img: ECO_IMG, desc: "Colour-coded 40cm flat-mop kit.", eco: true },
  { id: "lintfree", name: "LintFree Glass Cloths", cat: "Microfiber", img: SPRAY_IMG, desc: "Streak-free finish, pack of 25.", eco: false },
  { id: "forgeclean", name: "ForgeClean Industrial Degreaser", cat: "Degreasers", img: SPRAY_IMG, desc: "Heavy plant & floor formula, 20L.", eco: false },
  { id: "steamline", name: "SteamLine Heavy Duty Gel", cat: "Degreasers", img: ECO_IMG, desc: "Clinging gel for vertical plant steel.", eco: false },
  { id: "verde", name: "Verde Eco Concentrate", cat: "Green Tech", img: ECO_IMG, desc: "EU Ecolabel all-purpose, 1:100 dilution.", eco: true },
  { id: "biopure", name: "BioPure Plant-Based Spray", cat: "Green Tech", img: SPRAY_IMG, desc: "Ready-to-use daily surface spray.", eco: true },
];

export const Products = () => {
  const [tab, setTab] = useState("All");
  const visible = PRODUCTS.filter((p) => tab === "All" || p.cat === tab);

  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative py-24 sm:py-32 bg-navy-950 grain overflow-hidden"
    >
      <div className="absolute inset-0 spotlight" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-12 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8"
        >
          <div>
            <p className="eyebrow mb-4">Supplies & Formulations</p>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-ice-50 max-w-xl leading-snug">
              The same professional-grade kit{" "}
              <span className="text-gold-500">our own crews trust.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="product-filter-tabs">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`product-tab-${t.toLowerCase().replace(" ", "-")}`}
                onClick={() => setTab(t)}
                className={`font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2 rounded-full border transition-all duration-300 ${
                  tab === t
                    ? "bg-gold-500 text-navy-900 border-gold-500 font-bold"
                    : "border-white/10 text-ice-200/60 hover:border-gold-500/50 hover:text-gold-400"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.35 }}
                className="group relative bg-navy-800/60 border border-white/5 rounded-xl overflow-hidden hover:border-gold-500/40 transition-colors duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(247,184,1,0.15),transparent_70%)] z-10 pointer-events-none" />
                  <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {p.eco && (
                    <span className="absolute top-3 left-3 z-20 inline-flex items-center gap-1 bg-navy-900/80 backdrop-blur-sm border border-gold-500/30 text-gold-400 font-mono text-[9px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">
                      <Leaf className="w-3 h-3" /> Eco-Certified
                    </span>
                  )}
                </div>
                <div className="p-5">
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-gold-500/80 mb-1.5">
                    {p.cat}
                  </p>
                  <h3 className="font-heading font-semibold text-base text-ice-50 mb-1">
                    {p.name}
                  </h3>
                  <p className="text-xs text-ice-200/50 mb-4">{p.desc}</p>
                  <button
                    data-testid={`product-sds-${p.id}`}
                    onClick={() => toast.info("Safety Data Sheet will be emailed to you shortly.")}
                    className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ice-200/50 hover:text-gold-400 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" /> SDS Sheet
                  </button>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
